import React, { useState } from "react";
import {
  Box,
  Grid,
  Typography,
  TextField,
  MenuItem,
  FormControlLabel,
  RadioGroup,
  Radio,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";
import PageHeader from "../components/ui/PageHeader";
import CardComponent from "../components/ui/CardComponent";
import Sidebar from "../components/ui/Sidebar";
import { auth } from "../firebase-config";
import { useToast } from "../components/ui/ToastContext";
import ButtonComponent from "../components/ui/ButtonComponent";

const initialFormState = {
  fullName: "",
  phone: "",
  email: "",
  dob: "",
  address: "",
  city: "",
  state: "",
  zipCode: "",
  referredBy: "",
  contactMethod: "",
  prequal: {
    dbsImplant: "",
    epilepsy: "",
    brainTherapies: "",
    otherTherapies: "",
    substanceUse: "",
    upcomingSurgery: "",
  },
  adultQualifiers: {
    medications: "",
    stoppedMedications: "",
    tbiHistory: "",
    chronicConditions: "",
    sensitivity: "",
    stressors: "",
  },
};

const disqualifyingAnswers = {
  dbsImplant: "Yes",
  epilepsy: "Yes",
};

// 🔧 Helper to format ONLY prequal + adultQualifiers in the same style
const formatFormDataForNotes = (formData) => {
  if (!formData) return "";

  const lines = [];
  const safe = (v) => (v !== undefined && v !== null && String(v).trim() !== "" ? v : "N/A");

  // Only include these two sections
  ["prequal", "adultQualifiers"].forEach((section) => {
    if (formData[section] && typeof formData[section] === "object") {
      lines.push(`${section}:`);
      for (const [key, val] of Object.entries(formData[section])) {
        lines.push(`  - ${key}: ${safe(val)}`);
      }
      lines.push(""); // blank line between sections
    }
  });

  return lines.join("\n");
};

const ClientIntakeForm = () => {
  const { addToast } = useToast();
  const [formData, setFormData] = useState(initialFormState);
  const [submitting, setSubmitting] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [submittedData, setSubmittedData] = useState(null);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleNestedChange = (section, field, value) => {
    setFormData((prev) => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value,
      },
    }));
  };

  const isDisqualified = Object.entries(disqualifyingAnswers).some(
    ([key, disqualify]) => formData.prequal[key] === disqualify
  );

  const handleCopyToClipboard = () => {
    if (!submittedData) {
      addToast("No submitted data available to copy.", "warning");
      return;
    }
    const formatted = formatFormDataForNotes(submittedData);
    navigator.clipboard.writeText(formatted)
      .then(() => addToast("Form data copied to clipboard.", "success"))
      .catch(() => addToast("Failed to copy form data.", "error"));
  };

  const handleSubmit = async () => {
    if (!auth.currentUser) {
      alert("Authentication required. Please log in again.");
      return;
    }

    if (isDisqualified) {
      const confirmProceed = window.confirm(
        "This client is disqualified based on their responses.\n\nAre you sure you want to submit this form?"
      );
      if (!confirmProceed) return;
    }

    try {
      setSubmitting(true);
      const idToken = await auth.currentUser.getIdToken(true);

      // Step 1: Generate PDF
      const pdfResponse = await fetch(
        "https://generateformpdf-774073159618.us-central1.run.app",
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${idToken}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            formId: "prequal-intake",
            formData,
            logoUrl: "https://storage.googleapis.com/kinova/Kinova_transparent.png",
            themeColor: "#004080",
            headerText: "Client Intake & Pre-Qualification Form",
          }),
        }
      );

      const pdfResult = await pdfResponse.json();
      if (!pdfResponse.ok || !pdfResult.downloadUrl || !pdfResult.path) {
        throw new Error(pdfResult.message || "Failed to generate PDF.");
      }

      setPdfUrl(pdfResult.downloadUrl);
      addToast("Form submitted successfully! Your PDF is ready.", "success");

      // Step 2: Submit to Mindbody
      const mindbodyResponse = await fetch(
        "https://submittomindbody-774073159618.us-central1.run.app",
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${idToken}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            fullName: formData.fullName || "",
            email: formData.email || "",
            phone: formData.phone || "",
            dob: formData.dob || "",
            address: formData.address || "",
            city: formData.city || "",
            state: formData.state || "",
            zipCode: formData.zipCode || "",
            referredBy: formData.referredBy || "",
            formId: "prequal-intake",
            pdfPath: pdfResult.path,
            formData, // Send full formData
            formattedNotes: formatFormDataForNotes(formData), // ✅ Use same format for notes
          }),
        }
      );

      const mindbodyResult = await mindbodyResponse.json();

      if (!mindbodyResponse.ok) {
        addToast("Client synced to PDF, but Mindbody sync failed.", "warning");
      } else if (mindbodyResult.mindbodyIntegrationEnabled === false) {
        // Silently ignore — do not show any Mindbody-related messages
      } else {
        addToast("Client synced to Mindbody successfully.", "success");
      }

      setSubmittedData(formData);
      setFormData(initialFormState);

    } catch (error) {
      console.error("Submission failed:", error);
      alert("Submission failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="manage-users-container">
      <Sidebar />
      <div className="manage-users-content">
        <Box>
          <PageHeader title="Client Intake & Pre-Qualification Form" />
          <CardComponent>
            <Grid container spacing={3}>
              <Grid item xs={12}><Typography variant="h6">Client Information</Typography></Grid>

              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Full Name" value={formData.fullName} onChange={(e) => handleChange("fullName", e.target.value)} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Phone Number" value={formData.phone} onChange={(e) => handleChange("phone", e.target.value)} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Email Address" value={formData.email} onChange={(e) => handleChange("email", e.target.value)} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <DatePicker
                  label="Date of Birth"
                  value={formData.dob ? dayjs(formData.dob) : null}
                  onChange={(newValue) => handleChange("dob", newValue ? newValue.format("YYYY-MM-DD") : "")}
                  disableFuture
                  openTo="year"
                  views={["year", "month", "day"]}
                  renderInput={(params) => <TextField fullWidth {...params} />}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Address" value={formData.address} onChange={(e) => handleChange("address", e.target.value)} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="City" value={formData.city} onChange={(e) => handleChange("city", e.target.value)} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="State" value={formData.state} onChange={(e) => handleChange("state", e.target.value)} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Zip Code" value={formData.zipCode} onChange={(e) => handleChange("zipCode", e.target.value)} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Referred By" value={formData.referredBy} onChange={(e) => handleChange("referredBy", e.target.value)} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField select fullWidth label="Preferred Contact Method" value={formData.contactMethod} onChange={(e) => handleChange("contactMethod", e.target.value)}>
                  <MenuItem value="Phone">Phone</MenuItem>
                  <MenuItem value="Email">Email</MenuItem>
                  <MenuItem value="SMS">SMS</MenuItem>
                </TextField>
              </Grid>

              <Grid item xs={12}><Typography variant="h6">Pre-Qualification Questions</Typography>
              </Grid>
              {isDisqualified && (
                <Grid item xs={12}>
                  <Box p={2} bgcolor="#ffe6e6" border="1px solid #ff4d4d" borderRadius={2}>
                    <Typography color="error" fontWeight="bold">
                      This client is disqualified based on their responses. Please review carefully before proceeding.
                    </Typography>
                  </Box>
                </Grid>
              )}
              {[
                {
                    key: "dbsImplant",
                    label: "Do you currently have a DBS implant or any stimulation device in your head or body?",
                },
                {
                    key: "epilepsy",
                    label: "Do you have a history of seizures/epilepsy?",
                },
                {
                    key: "brainTherapies",
                    label: "Are you currently using any other brain-based therapies or applications?",
                    info: (
                    <>
                        • If ‘YES’, client must discontinue use of all other brain-based programs and therapies for a minimum of 2 weeks before beginning sessions. <br />
                        • If client is currently or has recently used ECT or TMS treatments, we advise a 1 year wait period after discontinuation before beginning BrainEcho sessions.
                    </>
                    ),
                },
                {
                    key: "otherTherapies",
                    label: "Are you using any other treatments or holistic remedies?",
                    info: "• If ‘YES’, client must discuss with a Tech Coach before scheduling sessions.",
                },
                {
                    key: "substanceUse",
                    label: "Do you use alcohol, THC/CBD products or recreational drugs?",
                    info:
                    "• If “Yes”, inform the client that total abstinence is strongly advised from alcohol (for 24 hours) & marijuana and recreational drugs (for 3-4 weeks) prior to the start of their sessions and for the 4 weeks following. Failure to abstain may result in limited or even negated results.",
                },
                {
                    key: "upcomingSurgery",
                    label: "Do you have any upcoming surgeries with general anesthesia?",
                    info:
                    "• If “Yes”, it is best that sessions are scheduled after the client has recovered from their surgery. General anesthesia directly impacts the brain and can interfere with session results and progress.",
                },
                ].map(({ key, label, info }) => (
                <Grid item xs={12} key={key}>
                    <Typography>{label}</Typography>
                    <RadioGroup
                    row
                    value={formData.prequal[key]}
                    onChange={(e) => handleNestedChange("prequal", key, e.target.value)}
                    >
                    <FormControlLabel value="Yes" control={<Radio />} label="Yes" />
                    <FormControlLabel value="No" control={<Radio />} label="No" />
                    </RadioGroup>
                    {formData.prequal[key] === "Yes" && info && (
                    <Box
                        mt={1}
                        p={2}
                        bgcolor="#fff4e5"
                        border="1px solid #ffc107"
                        borderRadius={2}
                    >
                        <Typography variant="body2">{info}</Typography>
                    </Box>
                    )}
                </Grid>
                ))}

              <Grid item xs={12}>
                <Typography variant="h6">Adult Qualifiers</Typography>
              </Grid>
              {[
                { key: "medications", label: "List any medications/supplements you currently take and why" },
                { key: "stoppedMedications", label: "Have you recently stopped any medications? Why?" },
                { key: "tbiHistory", label: "History of TBI/concussion (year and area affected)" },
                { key: "chronicConditions", label: "List any chronic or serious conditions and approximate onset date" },
                { key: "sensitivity", label: "Do you consider yourself sensitive in nature? Describe:" },
                { key: "stressors", label: "Do you have significant stressors currently? Describe:" },
              ].map(({ key, label }) => (
                <Grid item xs={12} key={key}>
                  <TextField
                    fullWidth
                    multiline
                    minRows={2}
                    label={label}
                    value={formData.adultQualifiers[key]}
                    onChange={(e) => handleNestedChange("adultQualifiers", key, e.target.value)}
                  />
                </Grid>
              ))}

              <Grid item xs={12}>
                <Box display="flex" justifyContent="flex-end" gap={2}>
                  <ButtonComponent label="Clear" onClick={() => setFormData(initialFormState)} disabled={submitting} />
                  <ButtonComponent label="Submit Form" onClick={handleSubmit} isLoading={submitting} />
                </Box>
              </Grid>

               {/* PDF Output & Copy Notes Button */}
              {pdfUrl && submittedData && ( // <-- You can also check for submittedData here
              <Grid item xs={12}>
                  <CardComponent>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                      <Typography variant="h6">PDF Summary</Typography>
                      <Box display="flex" gap={2}>
                      <ButtonComponent
                          label="Copy Form Data"
                          onClick={handleCopyToClipboard}
                      />
                      <ButtonComponent
                          label="Download PDF"
                          onClick={() => {
                          const link = document.createElement("a");
                          link.href = pdfUrl;
                          link.download = "client_intake_form.pdf";
                          link.click();
                          }}
                          />
                      </Box>
                  </Box>
                  {/* 👇 CHANGE formData to submittedData here */}
                  <Typography variant="subtitle1"><strong>Client:</strong> {submittedData.fullName || "N/A"}</Typography>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}><strong>Date:</strong> {dayjs().format("MMMM D, YYYY")}</Typography>
                  <Typography variant="body2" sx={{ color: "text.secondary" }}>
                      Your form was successfully submitted and saved as a downloadable PDF. Please download and review the document.
                  </Typography>
                  </CardComponent>
              </Grid>
              )}
            </Grid>
          </CardComponent>
        </Box>
      </div>
    </div>
  );
};

export default ClientIntakeForm;